import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  HTTP_STATUSES,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import {
  mdiAlertOutline,
  mdiChevronLeft,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { AddUpdateBuildTemplateModal } from "./AddUpdateBuildTemplateModal";
import { useBuildTemplateViewModel } from "./useBuildTemplateViewModel";

export function BuildTemplate() {
  const { hasPermission } = usePermissions();

  const {
    buildTemplates,
    stateManager,
    openAddModal,
    selectedItem,
    openDeleteModal,
    getBuildTemplates,
    updateBuildTemplate,
    deleteBuildTemplate,
    createBuildTemplate,
    setOpenAddModal,
    setSelectedItem,
    setOpenDeleteModal,
  } = useBuildTemplateViewModel();

  const hasEditPermission = hasPermission(PERMISSIONS.BUILD_TEMPLATE.UPDATE);
  const canDelete = hasPermission(PERMISSIONS.BUILD_TEMPLATE.DELETE);
  const canCreate = hasPermission(PERMISSIONS.BUILD_TEMPLATE.CREATE);

  const { classes } = useStyles();
  const theme = useTheme();

  const templateFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      groupable: false,
      flex: 1,
      renderCell: (p) =>
        hasEditPermission ? (
          <MiLink
            key={p.row.id}
            title={p.value}
            to={`/settings/milestone-management/build-types/${p.row.id}`}
          />
        ) : (
          p.value
        ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      renderCell: (p) => p.row.createdBy?.trim() || "N/A",
    },
    {
      field: "createdOn",
      headerName: "Created Date",
      renderCell: (p) => formatDate(p.row.createdOn, "DD MMMM YYYY"),
      flex: 1,
    },
    {
      field: "modifiedOn",
      headerName: "Last Updated",
      renderCell: (p) => formatDate(p.row.modifiedOn, "DD MMMM YYYY"),
      flex: 1,
    },
  ];

  const onModalClose = () => {
    setOpenAddModal(false);
    setSelectedItem(null);
  };

  const onCreate = async (data) => {
    onModalClose();
    const response = await createBuildTemplate(data);
    if (response.status === HTTP_STATUSES.OK) {
      getBuildTemplates();
    }
  };
  const onUpdate = async (data) => {
    onModalClose();
    const response = await updateBuildTemplate(selectedItem.id, {
      ...data,
      modifiedOn: selectedItem.modifiedOn,
    });
    if (response.status === HTTP_STATUSES.OK) {
      getBuildTemplates();
    }
  };
  const onDelete = async () => {
    const response = await deleteBuildTemplate(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
      name: selectedItem.name,
    });

    if (response.status === HTTP_STATUSES.OK) {
      getBuildTemplates();
      onDeleteCancel();
    }
  };

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.EDIT,
      onClick: () => {
        setSelectedItem(row);
        setOpenAddModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdiPencilOutline}
          size={1}
          color={theme.palette.green.main}
        />
      ),
      disabled: !hasEditPermission,
    },
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
      popperContent: row.isInUse ? (
        <Box className={classes.popper}>
          <MiIcon
            path={mdiAlertOutline}
            color={theme.palette.orange.main}
            size={2}
          />
          <Typography variant="body2">
            This {ENTITY_NAMES.BUILD_TEMPLATE} is in use and cannot be deleted.
          </Typography>
        </Box>
      ) : null,
      disabled: !canDelete || row.isInUse,
    },
  ];
  const onDeleteCancel = () => {
    setOpenDeleteModal(false);
    setSelectedItem(null);
  };

  useEffect(() => {
    getBuildTemplates();
  }, []);

  return (
    <>
      <MiDetailPageHeader
        title={ENTITY_NAMES.BUILD_TEMPLATES}
        titleIcon={mdiChevronLeft}
        loading={stateManager.isBusy()}
        actionComponent={
          canCreate && (
            <MiButton
              title={`Add ${ENTITY_NAMES.BUILD_TEMPLATE}`}
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{
                path: mdiPlus,
                iconSize: 0.8,
              }}
              data-testid="add-buildTemplate-btn"
              onClick={() => setOpenAddModal(true)}
            />
          )
        }
      />
      <MiBox height="100%" overflow="auto">
        <MiList
          columns={templateFieldset}
          data={buildTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={stateManager.isBusy()}
          getRowActions={getRowActions}
          disableRowSelectionOnClick
          data-testid="milestones-table"
        />
      </MiBox>
      <AddUpdateBuildTemplateModal
        open={openAddModal}
        onModalClose={onModalClose}
        onSubmit={selectedItem ? onUpdate : onCreate}
        loading={stateManager.isBusy()}
        selectedData={selectedItem}
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete the ${ENTITY_NAMES.BUILD_TEMPLATE}: ${selectedItem?.name}?`}
        open={openDeleteModal}
        handlePositive={onDelete}
        handleNegative={onDeleteCancel}
        textPositive={BUTTON_LABELS.DELETE}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  popper: {
    padding: theme.spacing(2),
    maxWidth: 240,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.orange.light,
    border: `1px solid ${theme.palette.orange.dark}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));
